import {
  Box,
  CircularProgress,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchIcon } from "../assets/svg";
import useSearch from "../hooks/useSearch";

function SearchResultsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";

  const [searchQuery, setSearchQuery] = useState(initialQuery);

  useEffect(() => {
    const query = searchParams.get("q") || "";
    setSearchQuery(query);
  }, [searchParams]);

  const { data, isFetching, enabled } = useSearch(searchQuery);
  const searchResults = enabled ? data?.products || [] : [];
  const isSearching = enabled && isFetching;

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const value = searchQuery.trim();
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: value });
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    const trimmed = value.trim();
    if (!trimmed) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: trimmed }, { replace: true });
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  return (
    <Container
      sx={{
        padding: { xs: "20px 16px 32px", md: "30px 60px 48px" },
        maxWidth: "1440px !important",
      }}>
      <Typography
        sx={{
          fontSize: { xs: "24px", md: "36px" },
          lineHeight: { xs: "30px", md: "44px" },
          fontWeight: 500,
          mb: 2,
          fontFamily: "Avenir Book",
        }}>
        Search Products
      </Typography>

      <Box
        sx={{
          width: "100%",
          borderRadius: "0px",
          border: "1px solid #E5E5E5",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}>
        <form onSubmit={handleSearchSubmit}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search for products..."
            value={searchQuery}
            onChange={(e) => handleSearchInput(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                paddingLeft: "12px",
                borderRadius: 0,
                fontSize: "16px",
                fontFamily: "Avenir Book",
                "& fieldset": { border: 0 },
                "&:hover fieldset": { border: 0 },
                "&.Mui-focused fieldset": { border: 0 },
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton type="submit" disabled={!searchQuery.trim()}>
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
        </form>

        {searchQuery.trim() && (
          <Box
            sx={{
              borderTop: "1px solid #E5E5E5",
              maxHeight: "calc(100vh - 260px)",
              overflowY: "auto",
            }}>
            {isSearching ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "40px",
                }}>
                <CircularProgress size={30} />
              </Box>
            ) : searchResults.length > 0 ? (
              <List sx={{ padding: 0 }}>
                {searchResults.map((product, index) => (
                  <ListItem
                    key={product.post_id || index}
                    disablePadding
                    sx={{
                      borderBottom:
                        index < searchResults.length - 1
                          ? "1px solid #F5F5F5"
                          : "none",
                    }}>
                    <ListItemButton
                      onClick={() => handleProductClick(product.post_id)}
                      sx={{
                        padding: { xs: "14px 12px", md: "16px 20px" },
                        "&:hover": { backgroundColor: "#F9F9F9" },
                      }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: { xs: 1.5, md: 2 },
                          width: "100%",
                        }}>
                        <Box
                          sx={{
                            width: { xs: "52px", md: "64px" },
                            height: { xs: "52px", md: "64px" },
                            flexShrink: 0,
                            borderRadius: "0px",
                            overflow: "hidden",
                            backgroundColor: "#F5F5F5",
                          }}>
                          <img
                            src={product.featured_image}
                            alt={product.name}
                            decoding="async"
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: { xs: "14px", md: "16px" },
                              fontWeight: 400,
                              color: "#000",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontFamily: "Avenir Book",
                            }}>
                            {product.name}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "40px",
                }}>
                <Typography
                  sx={{
                    color: "#7D7D7D",
                    fontSize: "14px",
                    fontFamily: "Avenir Book",
                  }}>
                  No products found for "{searchQuery}"
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default SearchResultsPage;
